/**
 * Admin panel layout component.
 *
 * Provides the main layout structure for the admin panel including header,
 * statistics cards, navigation, and content area. Fetches and displays admin
 * statistics on mount.
 *
 * Route: `/admin/*`
 * Requires authentication: Yes (admin only)
 *
 * @module AdminLayout
 */

import { useMemo } from 'react';
import { Outlet, NavLink, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Users,
  FileQuestion,
  FileText,
  BookOpen,
  BarChart3,
  Database,
  Shield,
  PlayCircle,
  MessageSquare,
  Sparkles,
  Info,
  KeyRound,
  CreditCard,
  Coins,
  HardDrive,
  Home,
  Loader2,
  MessageSquareHeart,
  ShieldAlert,
  UserCheck,
  Stethoscope,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';
import { useAdminDashboardStats } from '@/hooks/useAdminDashboardStats';
import { ROUTES } from '@/lib/routeConstants';
import { Button } from '@/components/ui/button';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';

export default function AdminLayout() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const location = useLocation();
  const { stats, loading } = useAdminDashboardStats();

  // Navigation items matching the original tabs
  const navItems = useMemo(
    () => [
      { path: ROUTES.ADMIN_USERS, icon: Users, label: 'Users' },
      { path: ROUTES.ADMIN_QUESTIONS, icon: FileQuestion, label: 'Questions' },
      { path: ROUTES.ADMIN_CONTEXTS, icon: FileText, label: 'Contexts' },
      { path: ROUTES.ADMIN_TITLES, icon: BookOpen, label: 'Titles' },
      { path: ROUTES.ADMIN_INTRODUCTION, icon: Info, label: 'Introduction' },
      { path: ROUTES.ADMIN_GENERATION, icon: Sparkles, label: 'Generation' },
      { path: ROUTES.ADMIN_SIMULATIONS, icon: PlayCircle, label: 'Simulations' },
      { path: ROUTES.ADMIN_ANSWERS, icon: MessageSquare, label: 'Answers' },
      { path: ROUTES.ADMIN_FEEDBACK, icon: MessageSquareHeart, label: t('admin.feedbackReview.navLabel') },
      { path: ROUTES.ADMIN_MODERATION, icon: ShieldAlert, label: t('admin.moderation.navLabel', { defaultValue: 'Moderation' }) },
      { path: ROUTES.ADMIN_HUMAN_REVIEWS, icon: UserCheck, label: t('humanReviewAdmin.nav', { defaultValue: 'Human Reviews' }) },
      { path: ROUTES.ADMIN_ANALYTICS, icon: BarChart3, label: 'Analytics' },
      { path: ROUTES.ADMIN_DIAGNOSTIC_REVIEW, icon: Stethoscope, label: t('diagnostic.review.nav', { defaultValue: 'Diagnostic Tags' }) },
      { path: ROUTES.ADMIN_TOKEN_USAGE, icon: Coins, label: 'Token Usage' },
      { path: ROUTES.ADMIN_ACTIVATION, icon: KeyRound, label: 'Activation' },
      { path: ROUTES.ADMIN_PLANS, icon: CreditCard, label: 'Plans' },
      { path: ROUTES.ADMIN_BACKUP, icon: HardDrive, label: 'Backup' },
    ],
    [t],
  );

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 md:px-16 py-4 sm:py-8">
        {/* Header */}
        <div className="mb-4 sm:mb-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex flex-col sm:flex-row items-center gap-4 w-full md:w-auto">
              <div className="text-center sm:text-left">
                <h1 className="font-ui text-2xl sm:text-3xl font-bold text-foreground flex items-center justify-center sm:justify-start gap-3">
                  <Shield className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />
                  Admin Panel
                </h1>
                <p className="font-ui text-muted-foreground mt-2 text-sm sm:text-base">
                  Manage content, users, and system settings
                </p>
              </div>
              <Link to={ROUTES.DASHBOARD}>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-10 w-10"
                  title="Go to Home"
                >
                  <Home className="h-5 w-5" />
                </Button>
              </Link>
            </div>
            <div className="flex items-start gap-3 text-center md:text-right">
              <LanguageSwitcher />
              <div>
                <p className="font-ui text-sm text-muted-foreground">
                  Welcome back,
                </p>
                <p className="font-ui font-semibold text-foreground">
                  {user?.username}
                </p>
                <Badge variant="default" className="mt-1">
                  Administrator
                </Badge>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-ui text-sm text-muted-foreground">
                    Total Questions
                  </p>
                  <p className="font-ui text-2xl font-bold text-primary">
                    {loading ? (
                      <Loader2 className="h-6 w-6 animate-spin inline" />
                    ) : (
                      stats.totalQuestions
                    )}
                  </p>
                </div>
                <FileQuestion className="h-8 w-8 text-primary" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-ui text-sm text-muted-foreground">
                    Total Contexts
                  </p>
                  <p className="font-ui text-2xl font-bold text-primary">
                    {loading ? (
                      <Loader2 className="h-6 w-6 animate-spin inline" />
                    ) : (
                      stats.totalContexts
                    )}
                  </p>
                </div>
                <FileText className="h-8 w-8 text-primary" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-ui text-sm text-muted-foreground">
                    Active Users
                  </p>
                  <p className="font-ui text-2xl font-bold text-primary">
                    {loading ? (
                      <Loader2 className="h-6 w-6 animate-spin inline" />
                    ) : (
                      stats.activeUsers
                    )}
                  </p>
                </div>
                <Users className="h-8 w-8 text-primary" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-ui text-sm text-muted-foreground">
                    Level Distribution
                  </p>
                  <p className="font-ui text-2xl font-bold text-primary">
                    {loading ? (
                      <Loader2 className="h-6 w-6 animate-spin inline" />
                    ) : (
                      `${stats.levelDistribution.length} levels`
                    )}
                  </p>
                </div>
                <Database className="h-8 w-8 text-primary" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Navigation */}
        <div className="overflow-x-auto mb-6 -mx-4 px-4 md:mx-0 md:px-0">
          <div className="flex lg:grid lg:grid-cols-12 gap-1 min-w-max lg:min-w-0 items-center rounded-md bg-muted p-1 text-muted-foreground">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;

              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={cn(
                    'font-ui inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-2 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 gap-2 min-h-[44px]',
                    isActive
                      ? 'bg-background text-foreground shadow-sm'
                      : 'hover:bg-background/50',
                  )}
                >
                  <Icon className="h-4 w-4 flex-shrink-0" />
                  <span className="hidden lg:inline">{item.label}</span>
                </NavLink>
              );
            })}
          </div>
        </div>

        {/* Page Content */}
        <Outlet />
      </div>
    </div>
  );
}
